import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule, MatSelectionList } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  InputSearchFilter,
  NgCommonModule,
  toSearchStream,
  TypedFormControl,
} from '@principle-theorem/ng-shared';
import { type ITag } from '@principle-theorem/principle-core/interfaces';
import {
  isSameRef,
  type DocumentReference,
  type WithRef,
} from '@principle-theorem/shared';
import { sortBy, uniqBy } from 'lodash';
import { PipesModule } from '../../pipes/pipes.module';

@Component({
  selector: 'pr-tag-filter',
  templateUrl: './tag-filter.component.html',
  styleUrls: ['./tag-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgCommonModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatChipsModule,
    MatTooltipModule,
    PipesModule,
    MatBadgeModule,
    MatListModule,
  ],
})
export class TagFilterComponent {
  tags = input.required<WithRef<ITag>[]>();
  selectedTags = input<DocumentReference<ITag>[]>([]);
  disabled = input(false);
  selectionChange = output<WithRef<ITag>[]>();
  sortedTags = computed(() => sortBy(uniqBy(this.tags(), 'name'), 'name'));
  selectedFiltersCount = computed(() =>
    this.selectedFilters().length > 10
      ? '10+'
      : this.selectedFilters().length.toString()
  );
  search: InputSearchFilter<WithRef<ITag>>;

  readonly selectedFilters = signal<WithRef<ITag>[]>([]);
  readonly tagSearchCtrl = new TypedFormControl<string>('');
  readonly selectionList =
    viewChild.required<MatSelectionList>('selectionList');
  readonly showClearButton = computed(() => this.selectedFilters().length > 0);

  constructor() {
    this.search = new InputSearchFilter<WithRef<ITag>>(
      toObservable(this.sortedTags),
      toSearchStream(this.tagSearchCtrl),
      ['name']
    );

    afterRenderEffect(() => this._syncSelectionWithInput());
  }

  onSelectionChange(): void {
    const list = this.selectionList();
    if (!list) {
      return;
    }

    const visibleSelected = list.selectedOptions.selected.map(
      (option) => option.value as WithRef<ITag>
    );
    const visibleOptions = list.options.map(
      (option) => option.value as WithRef<ITag>
    );
    const hiddenSelections = this.selectedFilters().filter(
      (tag) => !visibleOptions.some((visible) => isSameRef(visible, tag))
    );
    const merged = uniqBy(
      [...hiddenSelections, ...visibleSelected],
      (tag) => tag.ref.path
    );
    this.selectedFilters.set(merged);
    this.selectionChange.emit(merged);
  }

  clearTags(): void {
    this.tagSearchCtrl.setValue('');
    const list = this.selectionList();
    if (list) {
      list.deselectAll();
    }
    this.selectedFilters.set([]);
    this.selectionChange.emit([]);
  }

  compareWith(tagA: WithRef<ITag>, tagB: WithRef<ITag>): boolean {
    return isSameRef(tagA, tagB);
  }

  private _syncSelectionWithInput(): void {
    const inputTags = this.selectedTags();
    const allTags = this.sortedTags();
    const list = this.selectionList();

    if (!list || !list.options) {
      return;
    }

    if (inputTags.length === 0) {
      list.deselectAll();
      this.selectedFilters.set([]);
      return;
    }

    const selected = allTags.filter((tag) =>
      inputTags.some((inputTag) => isSameRef(tag.ref, inputTag))
    );

    this.selectedFilters.set(selected);

    list.deselectAll();
    selected.forEach((tag) => {
      const option = list.options.find((opt) =>
        isSameRef(opt.value as WithRef<ITag>, tag)
      );
      if (option) {
        option.selected = true;
      }
    });
  }
}
